import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Controls sit at radius 9 (`rounded-control`), not the card's 14. They carry no
 * shadow at rest — depth arrives on hover and focus, not before — and the focus
 * ring is the 3px steel outline from `@layer base`, never the browser default.
 */
export const inputVariants = cva(
  [
    "flex w-full bg-white rounded-control border px-3 text-control text-neutral-900",
    "transition-colors placeholder:text-neutral-400",
    "file:border-0 file:bg-transparent file:text-fine file:font-medium file:text-neutral-700",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border-neutral-200 hover:border-neutral-300",
        error: "border-danger-border text-danger-fg",
      },
      inputSize: {
        default: "h-10",
        sm: "h-8 text-fine",
        lg: "h-11",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  },
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, inputSize, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(inputVariants({ variant, inputSize, className }))}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export { Input };
